/**
 * carousel-shared.js
 * -------------------------------------------------------------------------
 * Carrusel genérico y reutilizable para el home de Tool Store.
 *
 * Objetivo: reemplazar la lógica de "carrusel" (flechas, swipe táctil,
 * puntos/dots, cálculo de tarjetas visibles según ancho de pantalla) que hoy
 * está copiada y reescrita por separado en cada sección:
 *   - slider-categorias-first      (Navegación por Categorías)
 *   - separador-margen             (Promoción estilo imagen)
 *   - section-categorias-automaticas (solo la PARTE de scroll/flechas)
 *   - section-favoritos-new-home     (solo la PARTE de scroll/flechas)
 *   - section-categorias-new-home  (Promoción de Combos, hoy con jQuery +
 *     owlCarousel — este módulo permite quitar esa dependencia)
 *
 * Este script NO reemplaza la lógica propia de cada sección (temporizador de
 * cuenta regresiva, carga de productos por AJAX, agregar al carrito, etc.).
 * Solo se encarga del "carrusel" en sí: mover la pista, flechas, puntos y
 * gesto de swipe. Cada sección sigue teniendo su propio script pequeño para
 * su lógica particular, pero deja de reimplementar el carrusel.
 *
 * CARGA (una sola vez para todo el sitio):
 *   En layout/theme.liquid, justo antes de </body>:
 *     {{ 'carousel-shared.js' | asset_url | script_tag }}
 *   (o con defer, según prefieran: <script src="{{ 'carousel-shared.js' | asset_url }}" defer></script>)
 *
 * USO en cada sección (dentro de un <script> pequeño, propio de la sección):
 *   <script>
 *     document.addEventListener('DOMContentLoaded', function () {
 *       ToolstoreCarousel.init('#mi-carrusel-1', {
 *         visible: { mobile: 1, tablet: 2, desktop: 4 },
 *         gap: 16,
 *         dots: true,
 *         swipe: true
 *       });
 *     });
 *   </script>
 *
 * MARCADO HTML ESPERADO dentro del contenedor que se pasa a init():
 *   <div id="mi-carrusel-1" class="ts-carousel">
 *     <button class="ts-carousel__prev" aria-label="Anterior">‹</button>
 *     <div class="ts-carousel__track">
 *       <div>...tarjeta 1...</div>
 *       <div>...tarjeta 2...</div>
 *       ...
 *     </div>
 *     <button class="ts-carousel__next" aria-label="Siguiente">›</button>
 *     <div class="ts-carousel__dots"></div>
 *   </div>
 *
 * Si el HTML de una sección ya existente usa otros nombres de clase para el
 * track/flechas/dots (por ejemplo ".slider-button-prev" en vez de
 * ".ts-carousel__prev"), NO hace falta renombrar el HTML: se puede pasar esos
 * selectores por opciones (ver trackSelector/prevSelector/nextSelector/
 * dotsSelector más abajo) para que apunten a las clases que ya existen.
 */
(function (window, document) {
  'use strict';

  function initOne(root, opts) {
    const settings = Object.assign(
      {
        visible: { mobile: 1, tablet: 2, desktop: 4 },
        gap: 16,
        dots: true,
        swipe: true,
        swipeThreshold: 40,
        autoplay: { enabled: false, interval: 3000, pauseOnHover: true },
        trackSelector: '.ts-carousel__track',
        prevSelector: '.ts-carousel__prev',
        nextSelector: '.ts-carousel__next',
        dotsSelector: '.ts-carousel__dots',
      },
      opts || {}
    );

    const track = root.querySelector(settings.trackSelector);
    const prevBtn = root.querySelector(settings.prevSelector);
    const nextBtn = root.querySelector(settings.nextSelector);
    const dotsWrap = settings.dots ? root.querySelector(settings.dotsSelector) : null;
    if (!track) return null;

    const cards = () => Array.from(track.children);

    let currentIndex = 0;
    let visibleCards = 1;
    let cardWidth = 0;
    let maxIndex = 0;

    function getVisibleCards() {
      const w = window.innerWidth;
      if (w < 768) return settings.visible.mobile;
      if (w < 1024) return settings.visible.tablet;
      return settings.visible.desktop;
    }

    function measure() {
      visibleCards = Math.max(1, getVisibleCards());
      const totalCards = cards().length;
      maxIndex = Math.max(0, totalCards - visibleCards);
      const containerWidth = root.clientWidth;
      cardWidth = (containerWidth - settings.gap * (visibleCards - 1)) / visibleCards;

      cards().forEach((card) => {
        card.style.width = cardWidth + 'px';
      });
      track.style.gap = settings.gap + 'px';

      currentIndex = Math.min(currentIndex, maxIndex);
      buildDots();
      update();
    }

    function update() {
      const offset = currentIndex * (cardWidth + settings.gap);
      track.style.transform = 'translateX(-' + offset + 'px)';
      if (prevBtn) prevBtn.disabled = currentIndex === 0;
      if (nextBtn) nextBtn.disabled = currentIndex >= maxIndex;
      updateDots();
    }

    function buildDots() {
      if (!settings.dots || !dotsWrap) return;
      const dotsNeeded = maxIndex + 1;
      if (dotsWrap.children.length === dotsNeeded) return;
      dotsWrap.innerHTML = '';
      for (let i = 0; i < dotsNeeded; i++) {
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'ts-carousel__dot';
        dot.setAttribute('aria-label', 'Ir a la posición ' + (i + 1));
        dot.addEventListener('click', () => goTo(i));
        dotsWrap.appendChild(dot);
      }
    }

    function updateDots() {
      if (!settings.dots || !dotsWrap) return;
      Array.from(dotsWrap.children).forEach((dot, i) => {
        dot.classList.toggle('is-active', i === currentIndex);
      });
    }

    function goTo(index) {
      currentIndex = Math.max(0, Math.min(index, maxIndex));
      update();
    }

    function next() {
      goTo(currentIndex + 1);
    }

    function prev() {
      goTo(currentIndex - 1);
    }

    if (prevBtn) prevBtn.addEventListener('click', prev);
    if (nextBtn) nextBtn.addEventListener('click', next);

    if (settings.swipe) {
      let touchStartX = 0;
      let touchEndX = 0;
      track.addEventListener(
        'touchstart',
        (e) => {
          touchStartX = e.changedTouches[0].screenX;
        },
        { passive: true }
      );
      track.addEventListener(
        'touchend',
        (e) => {
          touchEndX = e.changedTouches[0].screenX;
          const diff = touchStartX - touchEndX;
          if (Math.abs(diff) > settings.swipeThreshold) {
            diff > 0 ? next() : prev();
          }
        },
        { passive: true }
      );
    }

    let resizeTimer;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(measure, 150);
    });

    measure();

let autoplayTimer = null;
function stopAutoplay() {
if (autoplayTimer) { clearInterval(autoplayTimer); autoplayTimer = null; }
}
function startAutoplay() {
if (!settings.autoplay.enabled) return;
stopAutoplay();
autoplayTimer = setInterval(() => {
if (currentIndex >= maxIndex) { goTo(0); } else { next(); }
}, settings.autoplay.interval);
}
if (settings.autoplay.enabled) {
startAutoplay();
if (settings.autoplay.pauseOnHover) {
root.addEventListener('mouseenter', stopAutoplay);
root.addEventListener('mouseleave', startAutoplay);
}
}

    return { goTo, next, prev, refresh: measure, startAutoplay, stopAutoplay };
  }

  const ToolstoreCarousel = {
    instances: {},

    /**
     * @param {string|Element} selector  Selector CSS (o elemento) del/de los
     *   contenedor(es) raíz del carrusel.
     * @param {object} [opts]  Ver los valores por defecto en initOne().
     */
    init(selector, opts) {
      const roots =
        typeof selector === 'string'
          ? Array.from(document.querySelectorAll(selector))
          : [selector];

      roots.forEach((root) => {
        if (!root || root.dataset.tsCarouselInit === '1') return;
        root.dataset.tsCarouselInit = '1';
        const instance = initOne(root, opts);
        if (instance && root.id) this.instances[root.id] = instance;
      });
    },

    /** Vuelve a medir un carrusel ya inicializado (por ejemplo tras cargar
     *  productos por AJAX y cambiar la cantidad de tarjetas). */
    refresh(id) {
      if (this.instances[id]) this.instances[id].refresh();
    },
  };

  window.ToolstoreCarousel = ToolstoreCarousel;
})(window, document);
